<!--
  DashboardPage.vue — Phase 2 shell.

  Renders the 3-tab dashboard (Visão Geral / Op·Compromissos /
  Op·Service Orders). This phase wires only the shell — tabs,
  URL state, skeleton-rendering placeholders. Phase 3 plugs real data via
  per-tab composables (`src/composables/dashboard/useDashboard<Tab>.ts`).

  Tab content:
    - overview         — composed widgets driven by per-tab composables
    - op_compromissos  — embeds the legacy Compromissos dashboard wholesale
    - op_so            — embeds the Fechamentos page wholesale
                         (see tabs/OperacaoServiceOrdersTab.vue)

  URL state: ?tab=<key>. Bookmarkable and back-button friendly.

  Each tab is lazy-loaded via `defineAsyncComponent` so we don't pay the
  cost of mounting every tab on every navigation.

  Filter state is supplied by the global `FiltroHeaderLinks` in the topbar
  (rendered once in `src/ui/TopBar.vue`). Tabs react to `filter.updated`
  via their own composables in Phase 3 — this page only forwards the
  signal as a refresh trigger.

  Eventbus contract (declared here, implemented in Phase 3):
    dashboard.<tab>.loaded — composable emits on first successful fetch.
      Only overview still emits today; op_compromissos and op_so now
      embed full pages and do not fire this event.
-->
<template>
  <div class="dashboard-page" data-testid="dashboard-page">
    <TabView v-model="activeTab" :scrollable="true">
      <template #tablist>
        <div class="dashboard-tab-buttons" role="tablist" data-testid="dashboard-tablist">
          <Button
            v-for="tab in VISIBLE_TABS"
            :key="tab.key"
            size="small"
            :severity="activeTab === tab.key ? 'primary' : 'secondary'"
            :outlined="activeTab !== tab.key"
            :data-testid="`dashboard-tab-${tab.key}`"
            class="dashboard-tab-button"
            @click="selectTab(tab.key)"
          >
            {{ t(tab.label) }}
          </Button>
        </div>
      </template>

      <TabViewPanel v-for="tab in VISIBLE_TABS" :key="tab.key" :value="tab.key">
        <Suspense>
          <template #default>
            <component
              :is="tabComponents[tab.key]"
              v-if="activeTab === tab.key"
              :data-testid="`dashboard-tab-panel-${tab.key}`"
            />
          </template>
          <template #fallback>
            <div
              :data-testid="`dashboard-tab-skeleton-${tab.key}`"
              class="dashboard-tab-loading"
            >
              <DashboardSkeletonBlock height="200px" />
            </div>
          </template>
        </Suspense>
      </TabViewPanel>
    </TabView>
  </div>
</template>

<script setup lang="ts">
import { computed, defineAsyncComponent, onBeforeUnmount, onMounted, ref, watch, type Component } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { eventBus } from '../../eventBus';
import { config } from '../../services/configService';
import { log } from '../../utils/DebugLogger';
import DashboardSkeletonBlock from './widgets/DashboardSkeletonBlock.vue';
import TabView from '../../ui/TabView.vue';
import TabViewPanel from '../../ui/TabViewPanel.vue';

type TabKey = 'overview' | 'op_compromissos' | 'op_so';

interface TabDescriptor {
  key: TabKey;
  label: string;
  // Feature flag gating this tab; absent means always visible.
  feature?: string;
  // Temporarily suppressed from the UI regardless of feature flags.
  hidden?: boolean;
}

const TABS: ReadonlyArray<TabDescriptor> = [
  { key: 'overview',         label: 'Visão Geral', hidden: true },
  { key: 'op_compromissos',  label: 'Operação · Compromissos', feature: 'compromissos' },
  { key: 'op_so',            label: 'Vendas e Produção' },
] as const;

// Tabs shown in the UI: not explicitly hidden and not gated behind a disabled
// feature. Evaluated once at setup — feature flags are build-time constants.
const VISIBLE_TABS: ReadonlyArray<TabDescriptor> = TABS.filter(
  (tab) => !tab.hidden && (!tab.feature || config.isFeatureEnabled(tab.feature)),
);

const VALID_TAB_KEYS: ReadonlySet<TabKey> =
  new Set(VISIBLE_TABS.map((tab) => tab.key));

// Default tab, in order of precedence:
// 1. feature `dashboarddefaultcompromissos` (registro de features, opt-out por
//    tenant via AWRENV_SISTEMAAPI_DISABLED_FEATURES) → Operação · Compromissos,
//    quando essa aba está visível;
// 2. Vendas e Produção while Recebíveis is enabled;
// 3. the first visible tab (e.g. op_so when compromissos is disabled).
const DEFAULT_TAB: TabKey =
  config.isFeatureEnabled('dashboarddefaultcompromissos') && VALID_TAB_KEYS.has('op_compromissos')
    ? 'op_compromissos'
    : config.isFeatureEnabled('recebiveis') && VALID_TAB_KEYS.has('op_so')
      ? 'op_so'
      : (VISIBLE_TABS[0]?.key ?? 'op_so');

// Lazy-loaded tab content. defineAsyncComponent defers the chunk until the
// tab is first activated — keeps Visão Geral snappy when it is the entry.
const tabComponents: Record<TabKey, Component> = {
  overview:        defineAsyncComponent(() => import('./tabs/OverviewTab.vue')),
  op_compromissos: defineAsyncComponent(() => import('./tabs/OperacaoCompromissosTab.vue')),
  op_so:           defineAsyncComponent(() => import('./tabs/OperacaoServiceOrdersTab.vue')),
};

const { t } = useI18n();
const route = useRoute();
const router = useRouter();

function readTabFromRoute(): TabKey {
  const raw = route.query.tab;
  const candidate = Array.isArray(raw) ? raw[0] : raw;
  if (typeof candidate === 'string' && VALID_TAB_KEYS.has(candidate as TabKey)) {
    return candidate as TabKey;
  }
  return DEFAULT_TAB;
}

const activeTab = ref<TabKey>(readTabFromRoute());

function selectTab(next: TabKey): void {
  if (!VALID_TAB_KEYS.has(next)) {
    log('DashboardPage', 'selectTab', 'Ignoring invalid tab key', next);
    return;
  }
  if (activeTab.value === next) return;
  activeTab.value = next;
  // Push so back-button restores the prior tab.
  router.push({ query: { ...route.query, tab: next } });
}

// Keep activeTab in sync when the URL changes via browser navigation.
watch(() => route.query.tab, () => {
  const next = readTabFromRoute();
  if (next !== activeTab.value) activeTab.value = next;
});

// Surface filter changes for tabs that mount later — each tab's composable
// will subscribe to `filter.updated` independently in Phase 3. This page
// only logs to aid future debugging.
function onFilterUpdated(): void {
  log('DashboardPage', 'onFilterUpdated', 'Filter changed; tabs will refetch via their own composables');
}

onMounted(() => {
  log('DashboardPage', 'onMounted', 'Mounted with tab', activeTab.value);
  // Backfill the URL with the resolved tab when entering without a query.
  if (!route.query.tab) {
    router.replace({ query: { ...route.query, tab: activeTab.value } });
  }
  eventBus.on('filter.updated', onFilterUpdated);
});

onBeforeUnmount(() => {
  eventBus.off('filter.updated', onFilterUpdated);
});

defineExpose({ activeTab, selectTab, TABS });
</script>

<style scoped>
.dashboard-page {
  padding: 0;
}

.dashboard-tab-buttons {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  padding: 0.75rem 0;
  position: sticky;
  top: 0;
  z-index: 5;
}

.dashboard-tab-button {
  white-space: nowrap;
}

.dashboard-tab-loading {
  padding: 1rem 0;
}

/* PrimeVue Tabs renders tabpanels with a solid white background by default
   (--p-tabs-tabpanel-background). On the dashboard we want the app shell's
   surface-ground to show through, so flatten the panel surface to transparent. */
.dashboard-page :deep(.p-tabpanels),
.dashboard-page :deep(.p-tabpanel) {
  background: transparent;
}
</style>
